import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { LayoutDashboard, Pickaxe, Package, Wallet, Users, ListChecks, User, Shield, LogOut, Menu, X, Gem, Image, Megaphone, ShoppingCart, Trophy, CircleDollarSign, Briefcase } from "lucide-react";
import BannerSlot from "./BannerSlot";
import AdCodeSlot from "./AdCodeSlot";
import WarningBadge from "./WarningBadge";
import GameSounds from "../lib/sounds";
import API from "../lib/api";

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/mining", label: "Dig", icon: Pickaxe },
  { path: "/inventory", label: "Inventory", icon: Package },
  { path: "/workers", label: "Workers", icon: Users },
  { path: "/shop", label: "Shop", icon: ShoppingCart },
  { path: "/jobs", label: "Jobs", icon: Briefcase },
  { path: "/wallet", label: "Wallet", icon: Wallet },
  { path: "/tasks", label: "Tasks", icon: ListChecks },
  { path: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/profile", label: "Profile", icon: User },
];

const ADMIN_ITEMS = [
  { path: "/admin", label: "Admin Panel", icon: Shield },
  { path: "/admin/users", label: "Manage Users", icon: Users },
  { path: "/admin/economy", label: "Economy", icon: Wallet },
  { path: "/admin/finance", label: "Finance", icon: Package },
  { path: "/admin/content", label: "Content", icon: Image },
  { path: "/admin/assets", label: "Assets & Ads", icon: Megaphone },
  { path: "/admin/shop", label: "Shop Packs", icon: ShoppingCart },
  { path: "/admin/offerwalls", label: "Offerwalls", icon: Briefcase },
  { path: "/admin/faucetpay", label: "FaucetPay", icon: CircleDollarSign },
  { path: "/admin/email", label: "Email", icon: Shield },
  { path: "/admin/game-settings", label: "Game Settings", icon: Shield },
  { path: "/admin/ads", label: "Ad Codes", icon: CircleDollarSign },
  { path: "/admin/ptc", label: "PTC Ads", icon: Shield },
  { path: "/admin/tasks", label: "Tasks", icon: Briefcase },
  { path: "/admin/leaderboard", label: "Leaderboard", icon: Trophy },
  { path: "/admin/security", label: "Security (2FA)", icon: Shield },
  { path: "/admin/payment-proofs", label: "Payment Proofs", icon: CircleDollarSign },
];

export default function GameLayout({ children, title }) {
  const { user, logout } = useAuth();
  const { siteTitle } = useSiteSettings();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [sections, setSections] = useState({ jobs_enabled: true, tasks_enabled: true, leaderboard_enabled: true });
  const [footerData, setFooterData] = useState({ pages: [], socials: [] });
  const [proofsPageOn, setProofsPageOn] = useState(false);

  useEffect(() => {
    API.get("/settings/sections").then(r => setSections(r.data)).catch(() => {});
    API.get("/footer-data").then(r => setFooterData(r.data)).catch(() => {});
    API.get("/payment-proofs").then(r => setProofsPageOn(!!r.data?.settings?.page_enabled)).catch(() => {});
  }, []);

  const isActive = (path) => location.pathname === path;

  const filteredNav = NAV_ITEMS.filter(item => {
    if (item.path === "/jobs" && !sections.jobs_enabled) return false;
    if (item.path === "/tasks" && !sections.tasks_enabled) return false;
    if (item.path === "/leaderboard" && !sections.leaderboard_enabled) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0B0E14] flex" data-testid="game-layout">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#131722] border-r border-[#2A3241] transform transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} flex flex-col`}>
        <div className="p-6 border-b border-[#2A3241]">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
            <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
              <Gem className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#F8FAFC] tracking-tight">{siteTitle}</h1>
              <p className="text-xs text-[#64748B] font-mono-game">PLAY TO EARN</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNav.map(({ path, label, icon: Icon }) => (
            <Link key={path} to={path} onClick={() => setSidebarOpen(false)} data-testid={`nav-${label.toLowerCase()}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(path) ? "bg-[#00F0FF]/10 text-[#00F0FF] neon-border" : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1C212E]"}`}>
              <Icon className="w-4 h-4" />
              {label}
            </Link>
          ))}

          {user?.role === "admin" && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-4 text-xs font-mono-game text-[#64748B] tracking-[0.2em] uppercase">Admin</p>
              </div>
              {ADMIN_ITEMS.map(({ path, label, icon: Icon }) => (
                <Link key={path} to={path} onClick={() => setSidebarOpen(false)} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(path) ? "bg-[#FFD600]/10 text-[#FFD600] neon-border-gold" : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1C212E]"}`}>
                  <Icon className="w-4 h-4" />
                  {label}
                </Link>
              ))}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-[#2A3241]">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 rounded-full bg-[#1C212E] flex items-center justify-center text-[#00F0FF] text-xs font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#F8FAFC] truncate">{user?.name}</p>
              <p className="text-xs text-[#64748B] font-mono-game">{user?.coins?.toFixed(2) || 0} coins</p>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 w-full px-4 py-2 text-sm text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 glass-panel px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button className="lg:hidden text-[#94A3B8] hover:text-[#F8FAFC]" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <h2 className="text-xl font-bold text-[#F8FAFC]">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <WarningBadge />
            <button onClick={() => { const next = !soundOn; setSoundOn(next); GameSounds.toggle(next); if (next) GameSounds.click(); }} data-testid="sound-toggle"
              className={`p-2 rounded-lg border transition-all text-xs ${soundOn ? "border-[#10B981]/30 text-[#10B981] bg-[#10B981]/10" : "border-[#2A3241] text-[#64748B]"}`}>
              {soundOn ? "SFX" : "MUTE"}
            </button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1C212E] border border-[#2A3241]">
              <img src="https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/7c50250ec36470f2ff6073ef8315e6832e53889b40d11ee91bff8f28b2f0655e.png" alt="coin" className="w-5 h-5" />
              <span className="font-mono-game text-sm text-[#FFD600]" data-testid="header-coins">{user?.coins?.toFixed(2) || 0}</span>
            </div>
          </div>
        </header>
        <div className="p-6 animate-fade-in">
          <AdCodeSlot position="top" page={location.pathname.split("/")[1] || "dashboard"} />
          <BannerSlot page={location.pathname.split("/")[1] || "dashboard"} />
          {children}
          <AdCodeSlot position="bottom" page={location.pathname.split("/")[1] || "dashboard"} />
          {/* Footer */}
          <footer className="mt-8 pt-4 border-t border-[#2A3241]/30">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#64748B]">
              {/* Left: Page links */}
              <div className="flex flex-wrap items-center gap-3">
                {footerData.pages.map((pg, i) => (
                  <span key={pg.slug} className="flex items-center gap-3">
                    {i > 0 && <span>&middot;</span>}
                    <Link to={`/page/${pg.slug}`} className="hover:text-[#94A3B8]">{pg.title}</Link>
                  </span>
                ))}
                {proofsPageOn && (
                  <span className="flex items-center gap-3">
                    {footerData.pages.length > 0 && <span>&middot;</span>}
                    <Link to="/payment-proofs" className="hover:text-[#94A3B8]" data-testid="footer-payment-proofs-link">Payment Proofs</Link>
                  </span>
                )}
                {footerData.pages.length === 0 && !proofsPageOn && <span>{siteTitle}</span>}
              </div>
              {/* Right: Social icons */}
              {footerData.socials.length > 0 && (
                <div className="flex items-center gap-2">
                  {footerData.socials.map(s => (
                    <a key={s.link_id} href={s.url} target="_blank" rel="noopener noreferrer" title={s.platform}
                      className="w-7 h-7 rounded-md bg-[#1C212E] border border-[#2A3241]/50 flex items-center justify-center text-[10px] font-bold text-[#94A3B8] hover:text-[#00F0FF] hover:border-[#00F0FF]/30 transition-all">
                      {s.platform?.charAt(0)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}
